import { Input, message, Typography } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import Swal from 'sweetalert2'
import { createCase, deleteCase, updateCase } from '../../../api/aopsCase.api'
import { userState } from '../../../store/user'
import EmojiDropDown from '../EmojiDropDown'
import AopsCommonModalLayout from '../modal/AopsCommonModalLayout'
import AopsLevelStepInput from '../modal/AopsLevelStepInput'
import ModalStatusButtonGroup from '../modal/ModalStatusButtonGroup'
const { Text } = Typography
const placeholderText = [
  'ex) 발생',
  'ex) 대기',
  'ex) 환불 대기',
  'ex) 환불 완료',
  'ex) 홤불 처리 보고',
]
const CaseTrackBoardModal = ({
  open = false,
  onClose,
  fetchCaseList,
  caseDetail = null,
}) => {
  const navigate = useNavigate()
  const [isEdit, setIsEdit] = useState(false)
  const [user] = useRecoilState(userState)
  const [formData, setFormData] = useState({
    title: '',
    status: '2단계',
    emojiText: '✨',
  })
  const [originalStatus, setOriginalStatus] = useState(null) // 수정 모드일 때 원본 상태값
  const [stepList, setStepList] = useState([
    { stepNumber: 1, value: '' },
    { stepNumber: 2, value: '완료' },
  ])
  const prevStepListRef = useRef(stepList)

  // caseDetail이 있으면 수정 모드로 설정하고 데이터 바인딩 (상세 보기 시 getCaseDetail 응답에서 emojiText 로드 → 드롭다운 기본값)
  useEffect(() => {
    if (caseDetail && caseDetail.caseWork) {
      setIsEdit(true)
      const processStates = caseDetail.caseWork.caseWorkProcessStates || []
      const stepCount = processStates.length
      const statusText = `${stepCount}단계`

      const loadedEmojiText =
        caseDetail.caseWork.emojiText !== undefined &&
        caseDetail.caseWork.emojiText !== null &&
        String(caseDetail.caseWork.emojiText).trim() !== ''
          ? caseDetail.caseWork.emojiText
          : caseDetail.emojiText !== undefined &&
              caseDetail.emojiText !== null &&
              String(caseDetail.emojiText).trim() !== ''
            ? caseDetail.emojiText
            : '✨'

      setFormData({
        title: caseDetail.caseWork.caseTitle || '',
        status: statusText,
        emojiText: loadedEmojiText,
      })
      setOriginalStatus(statusText)

      const mappedStepList = processStates.map((item, idx) => ({
        stepNumber: item.step,
        value: idx === processStates.length - 1 ? '완료' : item.title || '',
      }))

      setStepList(mappedStepList)
      prevStepListRef.current = mappedStepList
    } else {
      setIsEdit(false)
      setOriginalStatus(null)
    }
  }, [caseDetail])

  // 상태값이 변경될 때 stepList 업데이트 (수정 모드가 아닐 때만)
  useEffect(() => {
    if (!isEdit) {
      const stepCount = parseInt(formData.status.replace('단계', ''))
      const newStepList = Array.from({ length: stepCount }, (_, index) => {
        const isLast = index === stepCount - 1
        return {
          stepNumber: index + 1,
          value: isLast ? '완료' : '', // 단계 수 변경 시 글씨 모두 초기화, 마지막만 '완료' 고정
        }
      })
      setStepList(newStepList)
      prevStepListRef.current = newStepList
    }
  }, [formData.status, isEdit])

  // stepList가 변경될 때 ref 업데이트
  useEffect(() => {
    prevStepListRef.current = stepList
  }, [stepList])

  const resetForm = () => {
    setIsEdit(false)
    setFormData({
      title: '',
      status: '2단계',
      emojiText: '✨',
    })
    setStepList([
      { stepNumber: 1, value: '' },
      { stepNumber: 2, value: '완료' },
    ])
    prevStepListRef.current = [
      { stepNumber: 1, value: '' },
      { stepNumber: 2, value: '완료' },
    ]
    onClose()
  }
  useEffect(() => {
    if (!open) resetForm()
  }, [open])

  const openErrorModal = (error) => {
    Swal.fire({
      customClass: { popup: 'custom-alert' },
      icon: 'error',
      title: '에러 발생',
      text: error,
    })
  }

  const validateForm = () => {
    if (!formData.title) {
      return false
    }
    if (!formData.status) {
      return false
    }
    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      message.error('모든 필드를 입력해주세요')
      return
    }
    const payload = {
      salusUserId: user?.id,
      title: formData?.title,
      totalSteps: stepList?.length,
      emojiText: formData?.emojiText ?? '',
      processList: stepList?.map((step, idx) => {
        const base = {
          title: idx === stepList.length - 1 ? '완료' : step.value,
          step: step.stepNumber,
        }
        const processId =
          isEdit &&
          (caseDetail?.caseWork?.caseWorkProcessStates?.[idx]?.id ??
            caseDetail?.caseWork?.caseWorkProcessStates?.[idx]?.processId)
        if (processId !== null) base.processId = processId
        return base
      }),
    }
    try {
      if (isEdit && caseDetail?.caseWork?.caseWorkId) {
        await updateCase(caseDetail.caseWork.caseWorkId, payload)
        message.success('케이스가 수정되었습니다.')
      } else {
        await createCase(payload)
        message.success('케이스가 생성되었습니다.')
      }
      resetForm()
      fetchCaseList()
    } catch (error) {
      openErrorModal(
        error?.response?.data?.detail?.[1]?.description ??
          '처리 중 오류가 발생했습니다.',
      )
    }
  }

  const handleDelete = async () => {
    if (!caseDetail?.caseWork?.caseWorkId) {
      message.error('삭제할 케이스 정보를 찾을 수 없습니다.')
      return
    }

    const result = await Swal.fire({
      customClass: { popup: 'custom-alert' },
      title: '케이스 삭제',
      html: ' 등록된 이슈가 모두 삭제됩니다.<br/>정말 삭제하시겠습니까?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
    })

    if (result.isConfirmed) {
      try {
        await deleteCase(caseDetail.caseWork.caseWorkId)
        message.success('케이스가 삭제되었습니다.')
        resetForm()
        navigate(-1)
        if (fetchCaseList) fetchCaseList()
      } catch (error) {
        console.error('케이스 삭제 실패:', error)
        const errorMessage =
          error?.response?.data?.detail?.[1]?.description ||
          error?.response?.data?.message ||
          '케이스 삭제 중 오류가 발생했습니다.'
        openErrorModal(errorMessage)
      }
    }
  }
  return (
    <AopsCommonModalLayout
      open={open}
      onClose={onClose}
      title={isEdit ? '케이스 상세보기' : '케이스 생성하기'}
    >
      <div className="form-container case-track-board-modal">
        <div className="form-wrapper">
          <div className="form-section">
            <label className="form-label">
              케이스 명 <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                }}
              >
                <EmojiDropDown
                  value={formData.emojiText}
                  defaultValue="✨"
                  onChange={(data) =>
                    setFormData({
                      ...formData,
                      emojiText:
                        typeof data === 'string' ? data : (data?.emoji ?? ''),
                    })
                  }
                />
                <Input
                  placeholder="케이스 명을 입력해주세요."
                  value={formData.title}
                  onChange={(e) => {
                    if (e.target.value.length >= 21) return
                    setFormData({ ...formData, title: e.target.value })
                  }}
                  className="form-input"
                  style={{ flex: 1, minWidth: 0 }}
                />
              </div>
              <div className="example-text-group">
                <Text className="example-text">케이스 업무란?</Text>
                <Text
                  className="example-text"
                  style={{
                    fontSize: '12px',
                    color: '#585F65',
                    fontWeight: '400',
                  }}
                >
                  일정 기간 동안 지속적으로 추적하여 관리해야 하는 업무를 하나의
                  맥락으로 묶어 관리하기 위한 업무 (ex: 물류, CS 등)
                </Text>
              </div>
            </div>
          </div>
          {/* 케이스 업무 */}
          <div className="form-section">
            <label className="form-label">
              상태값 <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ width: '100%' }}>
              <ModalStatusButtonGroup
                options={['2단계', '3단계', '4단계', '5단계']}
                value={formData.status}
                onChange={(value) => {
                  if (value.length >= 11) return
                  setFormData({ ...formData, status: value })
                }}
                disabled={isEdit}
              />
            </div>
          </div>
          <div className="case-level-content">
            <div className="case-level-content-header">
              <p className="title">상태값 단계 만들기</p>
              <p className="description">
                아래 예시처럼 추적해야 하는 업무의 단계를 작성해주세요
              </p>
            </div>
            <div className="case-step-container">
              {stepList.map((step, index) => {
                const isLastStep = index === stepList.length - 1
                return (
                  <AopsLevelStepInput
                    key={step.stepNumber}
                    stepNumber={step.stepNumber}
                    value={isLastStep ? '완료' : step.value}
                    onChange={(value) => {
                      if (isLastStep) return
                      if (value.length >= 25) return
                      const updatedStepList = stepList.map((item) =>
                        item.stepNumber === step.stepNumber
                          ? { ...item, value }
                          : item,
                      )
                      setStepList(updatedStepList)
                    }}
                    placeholder={
                      isLastStep ? '완료' : placeholderText[step.stepNumber - 1]
                    }
                    readOnly={isLastStep}
                  />
                )
              })}
            </div>
          </div>
        </div>
      </div>
      <div className="popup-footer">
        {isEdit ? (
          <>
            <button className="btn-cancel" onClick={handleDelete}>
              삭제
            </button>
            <button className="btn-submit" onClick={handleSubmit}>
              수정
            </button>
          </>
        ) : (
          <>
            <button className="btn-cancel" onClick={resetForm}>
              취소
            </button>
            <button className="btn-submit" onClick={handleSubmit}>
              등록
            </button>
          </>
        )}
      </div>
    </AopsCommonModalLayout>
  )
}

export default CaseTrackBoardModal
